import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import LOGO from "./assets/LOGO.png";
import IMGDreadManor from "./assets/DreadManor.webp";
import IMGServices from "./assets/bg-texture.png";
import Header from "./components/header/Header";
import Nav from "./components/nav/Nav";
import About from "./components/about/About";
import Stats from "./components/stats/Stats";
import Services from "./components/services/Services";
import Journey from "./components/journey/Journey";
import Projects from "./components/projects/Projects";
import Prototypes from "./components/prototypes/Prototypes";
import Experience from "./components/experience/Experience";
import ShowcaseBriefing from "./components/showcase/TopShowcaseBriefing";
import Contact from "./components/contact/Contact";
import Footer from "./components/footer/Footer";
import Trophies from "./components/trophies/Trophies";


const App = () => {
    const section = typeof window !== "undefined" ? window.location.hash || "#home" : "#home";
    const baseOrigin = typeof window !== "undefined" ? window.location.origin : "https://harshitsportfolio.netlify.app";
    const og = useMemo(() => {
        const base = {
            url: `${baseOrigin}${typeof window !== "undefined" ? `${window.location.pathname}${window.location.hash}` : "/"}`,
            image: LOGO,
            title: "Harshit Bhandari | Game Producer & Technical Producer",
            description: "Game Producer and Technical Producer with 3+ years leading commercial game development — production, cross-functional teams, and hands-on Unity from concept to launch.",
        };
        if (section === "#about") {
            return {
                ...base,
                title: "About Harshit Bhandari — Game Producer & Technical Producer",
                description: "3+ years, 8 commercial titles. Leading people, production, and technology while staying hands-on in Unity.",
            };
        }
        if (section === "#skills") {
            return {
                ...base,
                title: "Skills — Production, Agile, Unity, Multiplayer, Tooling",
                description: "Production and technical skills across shipped commercial games and live projects.",
                image: IMGDreadManor,
            };
        }
        if (section === "#projects" || section === "#experience") {
            return {
                ...base,
                title: "Work — Dread Manor, Dread Protocol & Commercial Games",
                description: "Featured productions and professional experience leading commercial game development.",
                image: IMGServices,
            };
        }
        return base;
    }, [section, baseOrigin]);

    const ogImageAbsolute = og.image && typeof og.image === "string"
        ? (og.image.startsWith("http") ? og.image : `${baseOrigin}${og.image}`)
        : og.image;

    return (
        <>
            <Helmet>
                <title>Harshit Bhandari | Game Producer & Technical Producer</title>
                <meta name="description" content="Harshit Bhandari — Game Producer & Technical Producer with hands-on Unity expertise. 3+ years leading cross-functional teams and shipping commercial games like Dread Manor & Dread Protocol from concept to launch." />
                <meta name="theme-color" content="#000000" />
                <meta name="robots" content="index, follow" />

                {/* Open Graph */}
                <meta property="og:title" content={og.title} />
                <meta property="og:description" content={og.description} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Harshit Bhandari Portfolio" />
                <meta property="og:locale" content="en_US" />
                <meta property="og:url" content={og.url} />
                <meta property="og:image" content={ogImageAbsolute} />
                <meta property="og:image:alt" content={og.title} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={og.title} />
                <meta name="twitter:description" content={og.description} />
                <meta name="twitter:image" content={ogImageAbsolute} />

                {/* Canonical */}
                <link rel="canonical" href={typeof window !== "undefined" ? `${window.location.origin}${window.location.pathname}` : ""} />

                {/* Favicon / Icons */}
                <link rel="icon" href={LOGO} />
                <link rel="apple-touch-icon" href={LOGO} />

                {/* JSON-LD: Person and WebSite */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Person",
                        name: "Harshit Bhandari",
                        jobTitle: "Game Producer & Technical Producer",
                        url: typeof window !== "undefined" ? `${window.location.origin}` : undefined,
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        name: "Harshit Bhandari Portfolio",
                        url: typeof window !== "undefined" ? `${window.location.origin}` : undefined,
                        inLanguage: "en",
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        name: og.title,
                        description: og.description,
                        url: og.url,
                        inLanguage: "en",
                        isPartOf: {
                            "@type": "WebSite",
                            name: "Harshit Bhandari Portfolio",
                        },
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? `${window.location.origin}/#home` : undefined },
                            { "@type": "ListItem", position: 2, name: "About", item: typeof window !== "undefined" ? `${window.location.origin}/#about` : undefined },
                            { "@type": "ListItem", position: 3, name: "Experience", item: typeof window !== "undefined" ? `${window.location.origin}/#experience` : undefined },
                            { "@type": "ListItem", position: 4, name: "Projects", item: typeof window !== "undefined" ? `${window.location.origin}/#projects` : undefined },
                            { "@type": "ListItem", position: 5, name: "Skills", item: typeof window !== "undefined" ? `${window.location.origin}/#skills` : undefined },
                            { "@type": "ListItem", position: 6, name: "Contact", item: typeof window !== "undefined" ? `${window.location.origin}/#contact` : undefined },
                        ],
                    })}
                </script>
            </Helmet>
            <Nav></Nav>
            <Header></Header>
            <About></About>
            <Services></Services>
            <Stats></Stats>
            <Journey></Journey>
            <Projects></Projects>
            <Prototypes></Prototypes>
            <Experience></Experience>
            <ShowcaseBriefing></ShowcaseBriefing>
            <Contact></Contact>
            <Footer></Footer>
            <Trophies></Trophies>
        </>
    );
};

export default App;
